// src/ProductionHouseView.js
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, FormControl, InputLabel, Select, MenuItem,
  TextField, Button, CircularProgress, Paper, Divider, Chip, Stack
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';

import FreelancerCard from './FreelancerCard';
import { calculateProgressAndDays } from './utils';
import { API_BASE, fetchJson } from './api';

import GroupIcon from '@mui/icons-material/Group';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SortIcon from '@mui/icons-material/Sort';
import LockIcon from '@mui/icons-material/Lock';
import HistoryIcon from '@mui/icons-material/History';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import RadarIcon from '@mui/icons-material/Radar';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Tooltip from '@mui/material/Tooltip';

// --- Helpers date: normalizza a mezzogiorno UTC per evitare slittamenti ---
const toUtcNoon = (dStr) => {
  if (!dStr) return null;
  const [y, m, d] = String(dStr).slice(0,10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(Date.UTC(y, (m - 1), d, 12, 0, 0, 0));
};

// True se il freelance è occupato nel giorno d (legge anche maestranze)
const isBusyOnDate = (freelancer, d) => {
  if (!d) return false;

  const prods =
    (Array.isArray(freelancer?.maestranze) && freelancer.maestranze) ||
    (Array.isArray(freelancer?.produzioni) && freelancer.produzioni) ||
    (Array.isArray(freelancer?.productions) && freelancer.productions) ||
    [];

  if (prods.length === 0) return false;

  return prods.some((p) => {
    const s = p.data_inizio_contratto || p.data_inizio || p.startDate || p.inizio;
    const e = p.data_fine_contratto   || p.data_fine   || p.endDate   || p.fine;

    const sd = s ? toUtcNoon(String(s).slice(0,10)) : null;
    const ed = e ? toUtcNoon(String(e).slice(0,10)) : null;

    if (sd && ed) return d >= sd && d <= ed;
    if (sd && !ed) return d >= sd;
    return false;
  });
};

// Per il testo del messaggio (formato IT leggibile)
const fmtIt = (yyyyMmDd) => {
  if (!yyyyMmDd) return '';
  const [y,m,d] = yyyyMmDd.split('-').map(Number);
  if (!y || !m || !d) return yyyyMmDd;
  return new Date(y, m-1, d).toLocaleDateString('it-IT', { day:'2-digit', month:'long', year:'numeric' });
};

// Helpers ----------------------------------------------------
const normArr = (x) =>
  Array.isArray(x) ? x.filter(Boolean) : (typeof x === 'string' && x ? [x] : []);

const toIsoDate = (s) => {
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{2})[\/-](\d{2})[\/-](\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toISOString().slice(0, 5);
};

// Token / Auth header ---------------------------------------
const getToken = (maybeToken) => {
  if (maybeToken) return maybeToken;
  try {
    return localStorage.getItem('token') || '';
  } catch {
    return '';
  }
};

const getAuthHeader = (t) => {
  const token = (t || '').trim();
  if (!token) return {};
  return { Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` };
};

const STATUS_OPTIONS = ['Tutti', 'Disponibile', 'Presto Disponibile', 'Occupato'];

// Dato rolesMaster = { Categoria: [ruoli...] }, produce [{category, roles}] preservando ordine e ortografia
const groupRoles = (rolesMaster) => {
  const out = [];
  if (rolesMaster && typeof rolesMaster === 'object') {
    Object.keys(rolesMaster).forEach((cat) => {
      let roles = Array.isArray(rolesMaster[cat]) ? rolesMaster[cat].filter(Boolean) : [];
      // Unifica Editor Video e ordina alfabeticamente
      if (roles.some(r => r.startsWith('Editor Video'))) {
        roles = roles.filter(r => !r.startsWith('Editor Video'));
        roles.push('Editor Video');
      }
      roles = roles.slice().sort((a, b) => a.localeCompare(b, 'it'));
      out.push({ category: cat, roles });
    });
  }
  return out;
};

// Stile condiviso pill input
const pillInput = {
  '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: 'rgba(255,255,255,0.04)', color: '#e0e0e0', fontSize: 13 },
  '& .MuiInputLabel-root': { fontSize: 13 },
};

const ProductionHouseView = ({ user, freelancers: freelancersProp, onSelectFreelancer, token }) => {
  const isSupervisor = user?.company_role === 'supervisor' || user?.company_role === 'admin';
  const canSendAlerts = user?.can_send_alerts !== false;
  const authToken = getToken(token);
  const navigate = useNavigate();

  // Per sticky “intelligente”
  const tallViewport = typeof window !== 'undefined' && window.innerHeight > 800;

  // Toggle/scroll filtri
  const filtersRef = useRef(null);
  const [showFilters, setShowFilters] = useState(true);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  // --- Gestione Produzioni: stato & handler ---
  const [showCreateProduction, setShowCreateProduction] = useState(false);
  const [newProdName, setNewProdName] = useState('');
  const [newProdClient, setNewProdClient] = useState('');
  const [clients, setClients] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const arr = await fetchJson('/clients');
        if (alive) setClients(Array.isArray(arr) ? arr.filter(Boolean) : []);
      } catch (_) {
        // se 404, l'autocomplete resta freeSolo
      }
    })();
    return () => { alive = false; };
  }, []);

  const handleCreateProduction = async () => {
    try {
      const payload = { name: newProdName, client: newProdClient || null };
      const newProd = await fetchJson('/productions', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    setShowCreateProduction(false);
    setNewProdName('');
    setNewProdClient('');
    // vai subito alla pagina della produzione appena creata
    navigate(`/productions/${newProd.id}`);
    } catch (e) {
      console.error(e);
      alert(`Errore nella creazione: ${e?.message || e}`);
    }
  };

  // --- Dati lista (se non arrivano via prop, li carico)
  const hasPropList = Array.isArray(freelancersProp) && freelancersProp.length > 0;
  const [freelancers, setFreelancers] = useState(hasPropList ? freelancersProp : []);
  const [loading, setLoading] = useState(!hasPropList);
  const [err, setErr] = useState('');

  // --- Filtri / Ordinamento
  const [roleFilter, setRoleFilter] = useState('Tutti');
  const [statusFilter, setStatusFilter] = useState('Tutti');
  const [companyFilter, setCompanyFilter] = useState('Tutti');
  const [cityFilter, setCityFilter] = useState('Tutti');

  // --- Ruoli master (per reparti/ortografia corretti) + versione raggruppata
  const [rolesMaster, setRolesMaster] = useState({});
  const groupedRoles = useMemo(() => groupRoles(rolesMaster), [rolesMaster]);

  // --- Ricerca/Alert (sezione in alto a destra)
  const [searchRuolo, setSearchRuolo] = useState('');
  const [searchDataInizio, setSearchDataInizio] = useState('');
  const [searchDataFine, setSearchDataFine] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null); // null: non ancora fatta
  const [alertMessage, setAlertMessage] = useState('');
  const [myAlerts, setMyAlerts] = useState([]);
  const [myAlertsLoading, setMyAlertsLoading] = useState(true);

  // Se la prop cambia, aggiorna la lista
  useEffect(() => {
    if (Array.isArray(freelancersProp) && freelancersProp.length > 0) {
      setFreelancers(freelancersProp);
      setLoading(false);
    }
  }, [freelancersProp]);

  // Fetch lista freelance se non arriva via prop
  useEffect(() => {
    if (Array.isArray(freelancersProp) && freelancersProp.length > 0) return;
    let abort = false;
    (async () => {
      try {
        setLoading(true);
        setErr('');
        const res = await fetch(`${API_BASE}/freelancers`, { headers: getAuthHeader(authToken) });
        const data = await res.json().catch(() => ({}));
       // Accetta più formati di risposta senza rompersi
       const list =
       Array.isArray(data) ? data
       : Array.isArray(data?.items) ? data.items
       : Array.isArray(data?.data) ? data.data
       : Array.isArray(data?.freelancers) ? data.freelancers
       : [];
        if (!abort) setFreelancers(list);
      } catch (e) {
        if (!abort) setErr('Impossibile caricare la lista freelance');
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => { abort = true; };
  }, [authToken, freelancersProp]);

  // Fetch ruoli master (reparti)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/ruoli`, { headers: getAuthHeader(authToken) });
        const data = await res.json().catch(() => ({}));
        if (!alive) return;
        if (data && typeof data === 'object') setRolesMaster(data);
      } catch { /* no-op */ }
    })();
    return () => { alive = false; };
  }, [authToken]);

  // Elenco società (per filtro)
  const companiesToShow = useMemo(() => {
    const raw = (freelancers || []).flatMap((f) => {
      const top = f?.azienda ? [f.azienda] : [];
      const fromJobs = normArr(f?.maestranze).map((m) => m?.azienda).filter(Boolean);
      return [...top, ...fromJobs];
    });
    return [...new Set(raw.filter(Boolean))].sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' }));
  }, [freelancers]);

  // Applica filtri + ordinamenti
  const filteredAndSortedFreelancers = useMemo(() => {
    let items = Array.isArray(freelancers) ? [...freelancers] : [];

    if (roleFilter && roleFilter !== 'Tutti') {
      const sel = String(roleFilter).trim().toLowerCase();
      items = items.filter((f) => {
        const rr = Array.isArray(f?.ruoli) ? f.ruoli : (f?.ruolo ? [f.ruolo] : []);
        return rr.map((r) => String(r || '').trim().toLowerCase()).includes(sel);
      });
    }

    if (statusFilter !== 'Tutti') {
      items = items.filter((f) => {
        const start = f?.data_inizio_contratto || f?.availability_start || '';
        const end = f?.data_fine_contratto || f?.availability_end || '';
        if (!start && !end) return true;
        const { status } = calculateProgressAndDays(start, end);
        return status === statusFilter;
      });
    }

    if (cityFilter !== 'Tutti') {
      items = items.filter(f => Array.isArray(f.cities) && f.cities.includes(cityFilter));
    }
    if (companyFilter !== 'Tutti') {
      items = items.filter((f) => {
        const top = f?.azienda === companyFilter;
        const fromJobs = normArr(f?.maestranze).some((m) => m?.azienda === companyFilter);
        return top || fromJobs;
      });
    }

    items.sort((a, b) => {
      const da = a?.updated_at || a?.created_at || '';
      const db = b?.updated_at || b?.created_at || '';
      return da > db ? -1 : da < db ? 1 : 0;
    });

    return items;
  }, [freelancers, roleFilter, statusFilter, companyFilter, cityFilter]);



  // Fetch alert inviati dal coordinatore
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setMyAlertsLoading(true);
        const res = await fetch(`${API_BASE}/alerts/mine?limit=5`, {
          headers: getAuthHeader(authToken),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (alive) setMyAlerts(Array.isArray(data) ? data : []);
      } catch { if (alive) setMyAlerts([]); }
      finally { if (alive) setMyAlertsLoading(false); }
    })();
    return () => { alive = false; };
  }, [authToken]);

  // === RADAR DISPONIBILITÀ ===
  // Freelance che si liberano nei prossimi 30 giorni, ordinati per data
  const radarFreelancers = useMemo(() => {
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const in30 = new Date(today);
    in30.setDate(today.getDate() + 30);

    return (freelancers || [])
      .map(f => {
        const { status } = calculateProgressAndDays(
          f?.data_inizio_contratto, f?.data_fine_contratto
        );
        // Già disponibile: nessuna data fine, o status Disponibile
        if (status === 'Disponibile' || !f?.data_fine_contratto) {
          return { ...f, _endDate: null, _daysLeft: 0 };
        }
        const endRaw = f?.data_fine_contratto || f?.availability_end || '';
        const ymd = String(endRaw).slice(0, 10);
        const [y, m, d] = ymd.split('-').map(Number);
        if (!y || !m || !d) return null;
        const endDate = new Date(y, m - 1, d, 12, 0, 0);
        if (endDate > in30) return null;
        const daysLeft = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
        return { ...f, _endDate: endDate, _daysLeft: daysLeft };
      })
      .filter(Boolean)
      .sort((a, b) => {
        // Già disponibili prima, poi per data crescente
        if (a._daysLeft === 0 && b._daysLeft > 0) return -1;
        if (b._daysLeft === 0 && a._daysLeft > 0) return 1;
        return a._daysLeft - b._daysLeft;
      })
      .slice(0, 5);
  }, [freelancers]);

  // Ricerca avanzata / invio alert
  const handleSearch = async () => {
    if (!searchRuolo || !searchDataInizio) {
      alert('Seleziona ruolo e data di inizio.');
      return;
    }

    setIsSearching(true);
    setSearchResults(null);

    try {
      const response = await fetch(`${API_BASE}/freelancers/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(authToken),
        },
        body: JSON.stringify({
          ruolo: searchRuolo,
          dataInizio: toIsoDate(searchDataInizio),
          dataFine: toIsoDate(searchDataFine),
        }),
      });

      let results = null;
      try { results = await response.json(); } catch (e) {}

      let arr = [];
      if (response.ok) {
        arr = Array.isArray(results) ? results
          : Array.isArray(results?.results) ? results.results
          : Array.isArray(results?.items) ? results.items
          : Array.isArray(results?.data) ? results.data
          : Array.isArray(results?.freelancers) ? results.freelancers
          : [];
      } else if (response.status === 404) {
        arr = (freelancers || []).filter((f) => {
          const roles = typeof f.ruoli !== 'undefined' ? f.ruoli : f.role || f.roles;
          const list = Array.isArray(roles) ? roles : String(roles || '').split(/[,;|/]|\s-\s/);
          return list
            .map((s) => String(s).trim().replace(/\s+/g, ' ').toLowerCase())
            .includes((searchRuolo || '').trim().toLowerCase());
        });
      }

      // Escludi chi è certamente occupato nel "Disponibile Dal"
      const availableFrom = toUtcNoon(searchDataInizio);
      const filtered = Array.isArray(arr) ? arr.filter(f => !isBusyOnDate(f, availableFrom)) : [];
      setSearchResults(filtered);

      // Messaggio Telegram predefinito (se vuoto)
      if (!alertMessage || alertMessage.trim().length === 0) {
        const societa = (companyFilter && companyFilter !== 'Tutti') ? companyFilter : 'Produzione';
        const di = fmtIt(searchDataInizio);
        const df = searchDataFine ? fmtIt(searchDataFine) : '';
        const msgLines = [
          '📢 NUOVO ALERT DI LAVORO 📢',
          '',
          `Società: ${societa}`,
          '',
          'Ruolo ricercato:',
          `${searchRuolo}`,
          '',
          'Periodo indicativo:',
          di && df ? `Dal ${di} al ${df}` : (di ? `Dal ${di}` : ''),
          '',
          'Cosa fare:',
          'Per candidarti o per maggiori info, clicca su Contatta.'
        ];
        setAlertMessage(msgLines.join('\n'));
      }
    } catch (err) {
      console.error('Errore ricerca freelance:', err);
      alert('Errore durante la ricerca.');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

 // In ProductionHouseView.js
const handleSendAlert = async () => {
  if (!alertMessage) {
    alert('Il messaggio non può essere vuoto.');
    return;
  }
  try {
    const resp = await fetch(`${API_BASE}/telegram/send-alert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader(authToken) },
      body: JSON.stringify({ message: alertMessage, ruolo: searchRuolo, date_from: toIsoDate(searchDataInizio) || null, date_to: toIsoDate(searchDataFine) || null }),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok || data?.ok === false) {
      const msg = data?.error || `HTTP ${resp.status}`;
      throw new Error(msg);
    }
    alert('Alert inviato su Telegram!');
  } catch (e) {                 // ⬅️ prima era "catch { ... }"
    alert(`Errore durante l'invio dell'alert: ${e?.message || e}`);
  }
};

  // Reset filtri
  const handleResetFilters = () => {
    setRoleFilter('Tutti');
    setStatusFilter('Tutti');
    setCompanyFilter('Tutti');
    setPage(1);
  };

  // Render ------------------------------------------------------------
  return (
    <Box sx={{ pb: 6, px: { xs: 2, md: 4 } }}>
      {/* ===================== RIGA 1: SOLO COLONNA SINISTRA (Gestione Produzioni) ===================== */}
      <Box
        sx={{
          display: { xs: 'block', md: 'grid' },
          gridTemplateColumns: { md: '1.6fr 1fr' },
          gap: 2,
          alignItems: 'start',
          mb: 2
        }}
      >
        {/* Colonna sinistra con il box produzioni */}
        <Box sx={{ gridColumn: { md: '1 / 2' }, mb: { xs: 3, md: 0 } }}>
          <Paper
            sx={{
              p: 1.5,
              border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: '10px',
              boxShadow: 'none',
              background: 'rgba(255,255,255,0.02)',
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>Gestione Produzioni</Typography>
                <Chip
                  size="small"
                  icon={true ? null : <LockIcon sx={{ fontSize: '13px !important' }} />}
                  label="Premium"
                  sx={{
                    bgcolor: 'rgba(212,165,80,0.12)',
                    border: '1px solid rgba(212,165,80,0.4)',
                    color: '#d4a550',
                    fontWeight: 700,
                    fontSize: 11,
                    height: 22,
                  }}
                />
              </Stack>
              <Stack direction="row" spacing={1.5}>
                {true ? (
                  <Button variant="outlined" onClick={() => navigate('/productions')}>
                    Vai a produzioni
                  </Button>
                ) : (
                  <Tooltip title="Disponibile con piano Premium" arrow>
                    <span>
                      <Button variant="outlined" disabled startIcon={<LockIcon sx={{ fontSize: 16 }} />} sx={{ opacity: 0.45 }}>
                        Vai a produzioni
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {true ? (
                  <Button size="small" variant="contained" onClick={() => navigate('/productions')}>
                    Nuova Produzione
                  </Button>
                ) : (
                  <Tooltip title="Disponibile con piano Premium" arrow>
                    <span>
                      <Button size="small" variant="contained" disabled startIcon={<LockIcon sx={{ fontSize: 16 }} />} sx={{ opacity: 0.45 }}>
                        Nuova Produzione
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            </Stack>
          </Paper>
        </Box>

        {/* Colonna destra vuota per questa riga */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }} />
      </Box>

      {/* ===================== RIGA 2: DUE COLONNE (Lista vs Ricerca) ===================== */}
      <Box
        sx={{
          display: { xs: 'block', md: 'grid' },
          gridTemplateColumns: { md: '1.6fr 1fr' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        {/* ===== COLONNA SINISTRA — Filtri lista + lista ===== */}
        <Box sx={{ gridColumn: { md: '1 / 2' }, mb: { xs: 3, md: 0 } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography
              variant="h6"
              sx={{ color: '#d4a550', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <GroupIcon fontSize="small" /> Ultime Attività
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              {!loading && (
                <Chip
                  size="small"
                  color="primary"
                  label={`${filteredAndSortedFreelancers.length} risultati`}
                  sx={{ fontWeight: 600, borderRadius: '14px' }}
                />
              )}

              {/* CHIP TOGGLE FILTRI */}
              <Chip
                clickable
                onClick={() => {
                  setShowFilters((v) => !v);
                  if (!showFilters) {
                    // sta per aprirsi → porta in vista
                    setTimeout(() => filtersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
                  }
                }}
                size="small"
                icon={<FilterAltIcon />}
                label={showFilters ? 'Nascondi filtri' : 'Mostra filtri'}
                sx={{
                  bgcolor: 'rgba(212,165,80,0.08)',
                  border: '1px solid rgba(212,165,80,0.25)',
                  fontWeight: 600,
                  borderRadius: '14px',
                }}
                aria-controls="filters-panel"
                aria-expanded={showFilters ? 'true' : 'false'}
              />

              <Button size="small" variant="outlined" onClick={handleResetFilters}>
                Reset filtri
              </Button>
            </Stack>
          </Stack>
          <Divider sx={{ mb: 2, opacity: 0.4 }} />

          {/* PANEL FILTRI COLLASSABILE */}
          {showFilters && (
            <Paper
              id="filters-panel"
              ref={filtersRef}
              elevation={0}
              sx={{
                p: 1.5,
                mb: 2,
                backgroundColor: 'rgba(255,255,255,0.02)',
                borderRadius: '10px',
                border: '1px solid rgba(255,255,255,0.07)',
                boxShadow: 'none',
              }}
            >
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                {/* RUOLO (lista) */}
                <FormControl variant="outlined" size="small" sx={{ minWidth: 240, ...pillInput }}>
                  <InputLabel>Ruolo</InputLabel>
                  <Select
                    value={roleFilter}
                    label="Ruolo"
                    onChange={(e) => setRoleFilter(e.target.value || 'Tutti')}
                  >
                    <MenuItem value="Tutti">
                      <em>Tutti i ruoli</em>
                    </MenuItem>
                    {groupedRoles.map(({ category, roles }) => [
                      <MenuItem
                        key={`hdr-list-${category}`}
                        disabled
                        sx={{
                          fontWeight: 'bold',
                          fontStyle: 'italic',
                          color: '#d4a550 !important',
                          opacity: 1,
                          backgroundColor: '#1e2128',
                        }}
                      >
                        {category}
                      </MenuItem>,
                      ...roles.map((role) => (
                        <MenuItem key={`list-${category}-${role}`} value={role} sx={{ pl: 4 }}>
                          {role}
                        </MenuItem>
                      )),
                    ])}
                  </Select>
                </FormControl>

                {/* STATO */}
                <FormControl variant="outlined" size="small" sx={{ minWidth: 180, ...pillInput }}>
                  <InputLabel>Stato</InputLabel>
                  <Select value={statusFilter} label="Stato" onChange={(e) => setStatusFilter(e.target.value)}>
                    {STATUS_OPTIONS.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status === 'Tutti' ? <em>Tutti gli stati</em> : status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* SOCIETÀ */}
                <FormControl variant="outlined" size="small" sx={{ minWidth: 200, ...pillInput }}>
                  <InputLabel>Società</InputLabel>
                  <Select value={companyFilter} label="Società" onChange={(e) => setCompanyFilter(e.target.value)}>
                    <MenuItem value="Tutti">
                      <em>Tutte le società</em>
                    </MenuItem>
                    {companiesToShow.map((company) => (
                      <MenuItem key={company} value={company}>
                        {company}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/* CITTÀ */}
                <FormControl variant="outlined" size="small" sx={{ minWidth: 180, ...pillInput }}>
                  <InputLabel>Città</InputLabel>
                  <Select value={cityFilter} label="Città" onChange={(e) => setCityFilter(e.target.value)}>
                    <MenuItem value="Tutti"><em>Tutte le città</em></MenuItem>
                    {['Alessandria','Ancona','Aosta','Arezzo','Asti','Bari','Benevento','Bergamo','Bolzano','Bologna','Brescia','Brindisi','Cagliari','Campobasso','Caserta','Catania','Catanzaro','Como','Cosenza','Cuneo','Estero','Ferrara','Firenze','Foggia','Frosinone','Genova',"L'Aquila",'La Spezia','Latina','Lecce','Livorno','Messina','Milano','Modena','Napoli','Novara','Nuoro','Padova','Palermo','Parma','Pescara','Perugia','Pisa','Potenza','Ravenna','Reggio Calabria','Reggio Emilia','Rieti','Rimini','Roma','Salerno','Sassari','Savona','Siena','Taranto','Teramo','Torino','Trento','Treviso','Trieste','Udine','Varese','Venezia','Verona','Vicenza','Viterbo'].map(c => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* ORDINA */}
                
              </Box>
            </Paper>
          )}

          {/* LISTA */}
          {loading && <Typography>Caricamento…</Typography>}
          {err && <Typography color="error">{err}</Typography>}

          {!loading && (
            <Box>
              {filteredAndSortedFreelancers.map((freelancer) => (
                <FreelancerCard
                  key={freelancer.id}
                  freelancer={freelancer}
                  onCardClick={() => onSelectFreelancer?.(freelancer.freelancer_id ?? freelancer.id)}
                />
              ))}

              {filteredAndSortedFreelancers.length === 0 && (
                <Typography sx={{ textAlign: 'center', mt: 6, color: '#9e9e9e' }}>
                Nessun freelance trovato con i filtri correnti o nessun dato ricevuto dall’API.
                </Typography>
              )}
            </Box>
          )}
        </Box>
        {/* ===== FINE COLONNA SINISTRA ===== */}

        {/* ===== COLONNA DESTRA — Radar + Ricerca ===== */}
        <Box
          sx={{
            gridColumn: { md: '2 / 3' },
            position: { md: tallViewport ? 'sticky' : 'static' },
            top: { md: 16 },
          }}
        >

          {/* === RADAR DISPONIBILITÀ === */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ color: '#d4a550', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <RadarIcon fontSize="small" /> Radar Disponibilità
            </Typography>
            <Chip
              size="small"
              label="Prossimi 30 giorni"
              sx={{ bgcolor: 'rgba(212,165,80,0.08)', border: '1px solid rgba(212,165,80,0.25)', fontWeight: 600, borderRadius: '14px', color: '#d4a550', fontSize: 11 }}
            />
          </Stack>
          <Divider sx={{ mb: 2, opacity: 0.4 }} />

          <Paper elevation={0} sx={{ p: 2, mb: 3, backgroundColor: '#13161c', borderRadius: '16px', border: '1px solid rgba(212,165,80,0.2)', boxShadow: '0 4px 24px rgba(0,0,0,0.3)' }}>
            {radarFreelancers.length === 0 ? (
              <Typography sx={{ color: '#888', fontSize: 13, textAlign: 'center', py: 2 }}>
                Nessun freelance libero al momento o che si liberi nei prossimi 30 giorni.
              </Typography>
            ) : (
              <Stack spacing={0.5}>
                {radarFreelancers.map((f, i) => {
                  const name = `${f?.nome || ''} ${f?.cognome || ''}`.trim();
                  const roles = Array.isArray(f?.ruoli) ? f.ruoli : (f?.ruolo ? [f.ruolo] : []);
                  const isVeryClose = f._daysLeft <= 7;
                  return (
                    <Box
                      key={f.id ?? i}
                      onClick={() => onSelectFreelancer?.(f.freelancer_id ?? f.id)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        px: 1.5, py: 1, borderRadius: 2, cursor: 'pointer',
                        transition: 'background .15s',
                        '&:hover': { bgcolor: 'rgba(212,165,80,0.07)' },
                        borderBottom: i < radarFreelancers.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                      }}
                    >
                      {/* Indicatore giorni */}
                      <Box sx={{
                        minWidth: 44, textAlign: 'center',
                        bgcolor: f._daysLeft === 0 ? 'rgba(76,175,80,0.15)' : isVeryClose ? 'rgba(212,165,80,0.18)' : 'rgba(255,255,255,0.05)',
                        borderRadius: 1.5, py: 0.5,
                      }}>
                        {f._daysLeft === 0 ? (
                          <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#4caf50', lineHeight: 1.4, px: 0.5 }}>
                            Libero
                          </Typography>
                        ) : (
                          <>
                            <Typography sx={{ fontSize: 15, fontWeight: 800, color: isVeryClose ? '#d4a550' : '#aaa', lineHeight: 1 }}>
                              {f._daysLeft}
                            </Typography>
                            <Typography sx={{ fontSize: 9, color: '#666', lineHeight: 1 }}>gg</Typography>
                          </>
                        )}
                      </Box>

                      {/* Nome + ruolo */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#e0e0e0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {name || '—'}
                        </Typography>
                        {roles[0] && (
                          <Typography sx={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {roles[0]}
                          </Typography>
                        )}
                      </Box>

                      {/* Data */}
                      <Typography sx={{ fontSize: 11, color: '#666', whiteSpace: 'nowrap' }}>
                        {f._endDate ? f._endDate.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }) : 'Ora'}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Paper>

          {/* === EVENTI DELLA SETTIMANA === */}
          <EventiWidgetProd />

          {/* === RICERCA / ALERT (collassabile) === */}
          <Box sx={{ display: (isSupervisor || canSendAlerts) ? 'block' : 'none' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ color: '#d4a550', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <WorkOutlineIcon fontSize="small" /> Cerca & Invia Alert
            </Typography>
            {!!searchResults && (
              <Chip
                size="small"
                color={searchResults.length > 0 ? 'success' : 'default'}
                label={searchResults.length > 0 ? `${searchResults.length} trovati` : 'Nessun risultato'}
                sx={{ bgcolor: 'rgba(212,165,80,0.08)', border: '1px solid rgba(212,165,80,0.25)', fontWeight: 600, borderRadius: '14px', color: '#d4a550' }}
              />
            )}
          </Stack>
          <Divider sx={{ mb: 2, opacity: 0.4 }} />

          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 4,
              backgroundColor: '#13161c',
              borderRadius: '16px',
              border: '1px solid rgba(212,165,80,0.2)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
            }}
          >
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControl variant="outlined" size="small" sx={{ minWidth: 260, ...pillInput }}>
                <InputLabel>Ruolo Richiesto</InputLabel>
                <Select value={searchRuolo} label="Ruolo Richiesto" onChange={(e) => setSearchRuolo(e.target.value)}>
                  {groupedRoles.map(({ category, roles }) => [
                    <MenuItem
                      key={`hdr-${category}`}
                      disabled
                      sx={{
                        fontWeight: 'bold',
                        fontStyle: 'italic',
                        color: '#d4a550 !important',
                        opacity: 1,
                        backgroundColor: '#1e2128',
                      }}
                    >
                      {category}
                    </MenuItem>,
                    ...roles.map((role) => (
                      <MenuItem key={`${category}-${role}`} value={role} sx={{ pl: 4 }}>
                        {role}
                      </MenuItem>
                    )),
                  ])}
                </Select>
              </FormControl>

              <TextField
                label="Disponibile Dal"
                type="date"
                size="small"
                value={searchDataInizio}
                onChange={(e) => setSearchDataInizio(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ ...pillInput, minWidth: 180 }}
              />
              <TextField
                label="Fino Al (obbligatorio)"
                type="date"
                size="small"
                value={searchDataFine}
                onChange={(e) => setSearchDataFine(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ ...pillInput, minWidth: 200 }}
              />

              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={isSearching || !searchRuolo || !searchDataInizio || !searchDataFine}
                sx={{ height: 40, borderRadius: '20px' }}
              >
                {isSearching ? <CircularProgress size={22} /> : 'Cerca Disponibilità'}
              </Button>
            </Box>

            {searchResults && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                {searchResults.length > 0 ? (
                  <Box>
                    <Typography sx={{ color: '#e0e0e0', fontWeight: 600, mb: 1 }}>
                      Trovati {searchResults.length} freelance compatibili:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {searchResults.map((freelancer) => (
                        <Button
                          key={freelancer.id}
                          variant="outlined"
                          size="small"
                          startIcon={<CalendarMonthIcon />}
                          onClick={() => onSelectFreelancer?.(freelancer.freelancer_id ?? freelancer.id)}
                          sx={{ borderRadius: '16px' }}
                        >
                          {(freelancer.nome || '') + ' ' + (freelancer.cognome || '')}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Typography sx={{ color: '#9e9e9e' }}>
                    Nessun freelance presente tra i risultati. Puoi inviare un alert generico.
                  </Typography>
                )}

                <TextField
                  label="Messaggio dell'Alert (editabile)"
                  multiline
                  rows={10}
                  fullWidth
                  value={alertMessage}
                  onChange={(e) => setAlertMessage(e.target.value)}
                  sx={{ mt: 2, mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: '#1a1d23', color: '#e0e0e0' } }}
                />

                {!isSupervisor && (
                  <Box sx={{ mb: 1, p: 1.5, borderRadius: 2, background: 'rgba(212,165,80,0.07)', border: '1px solid rgba(212,165,80,0.2)' }}>
                    <Typography variant="caption" sx={{ color: '#d4a550', fontFamily: 'Space Mono, monospace', fontSize: '10px', letterSpacing: '0.1em' }}>
                      🔒 FUNZIONE RISERVATA AI SUPERVISOR
                    </Typography>
                  </Box>
                )}
                <Button
                  variant="contained"
                  endIcon={<SendIcon />}
                  onClick={handleSendAlert}
                  disabled={!isSupervisor}
                  sx={{
                    width: '100%',
                    borderRadius: '20px',
                    backgroundColor: isSupervisor ? '#d4a550' : '#3a3a4a',
                    color: isSupervisor ? '#0e1116' : '#6a6a7a',
                    fontWeight: 700,
                    '&:hover': { backgroundColor: isSupervisor ? '#c49440' : '#3a3a4a' },
                    '&.Mui-disabled': { backgroundColor: '#2a2a3a', color: '#5a5a6a' },
                  }}
                >
                  Invia Alert
                </Button>
              </Box>
            )}
          </Paper>

          {/* === STORICO ALERT === */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 3, mb: 1 }}>
            <HistoryIcon sx={{ color: '#d4a550', fontSize: 20 }} />
            <Typography variant="h6" sx={{ color: '#d4a550', fontWeight: 700 }}>
              I Tuoi Alert
            </Typography>
            <Chip size="small" label="Ultimi 5" sx={{ bgcolor: 'rgba(212,165,80,0.08)', border: '1px solid rgba(212,165,80,0.25)', color: '#d4a550', fontSize: 11, height: 20 }} />
          </Stack>
          <Divider sx={{ mb: 2, opacity: 0.4 }} />

          <Paper elevation={0} sx={{ p: 2, backgroundColor: '#13161c', borderRadius: '16px', border: '1px solid rgba(212,165,80,0.2)', boxShadow: '0 4px 24px rgba(0,0,0,0.3)' }}>
            {myAlertsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} sx={{ color: '#d4a550' }} />
              </Box>
            ) : myAlerts.length === 0 ? (
              <Typography sx={{ color: '#888', fontSize: 13, textAlign: 'center', py: 2 }}>
                Nessun alert inviato ancora.
              </Typography>
            ) : (
              <Stack spacing={0}>
                {myAlerts.map((alert, i) => {
                  const date = alert.created_at ? new Date(alert.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';
                  const macro = alert.category || '—';
                  const ruolo = alert.ruolo || '—';
                  return (
                    <Box key={alert.id} sx={{
                      px: 1.5, py: 1, borderRadius: 2,
                      borderBottom: i < myAlerts.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                    }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 0.3 }}>
                            <NotificationsActiveIcon sx={{ fontSize: 13, color: '#d4a550' }} />
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#e0e0e0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {ruolo}
                            </Typography>
                          </Stack>
                          <Chip size="small" label={macro} sx={{ height: 18, fontSize: 10, bgcolor: 'rgba(212,165,80,0.07)', color: '#d4a550', border: '1px solid rgba(212,165,80,0.2)' }} />
                        </Box>
                        <Typography sx={{ fontSize: 11, color: '#666', whiteSpace: 'nowrap', ml: 1 }}>
                          {date}
                        </Typography>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Paper>

        </Box>
          </Box>
        {/* ===== FINE COLONNA DESTRA ===== */}
      </Box>
    </Box>
  );
};


function EventiWidgetProd() {
  const [events, setEvents] = React.useState([]);
  const nav = useNavigate();

  React.useEffect(() => {
    const token = localStorage.getItem('jkl_token') || localStorage.getItem('token');
    fetch('/api/events/week', { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.json())
      .then(d => setEvents(d.events || []))
      .catch(() => {});
  }, []);

  function formatDate(d) {
    return new Date(d).toLocaleDateString('it-IT', { weekday:'short', day:'numeric', month:'short' });
  }

  return (
    <Box sx={{ mb: 2, p: 2, borderRadius: 2, border: '1px solid rgba(212,165,80,0.2)', background: 'rgba(212,165,80,0.04)' }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#d4a550', mb: 1.5 }}>
        Prossimi eventi
      </Typography>
      {events.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: 'rgba(232,230,223,0.35)' }}>Nessun evento nei prossimi 20 giorni.</Typography>
      ) : (
        events.map(e => (
          <Box key={e.id} onClick={() => nav(`/eventi/${e.id}`)}
            sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', py: 1, borderBottom: '1px solid rgba(255,255,255,0.05)', cursor:'pointer', '&:last-child': { borderBottom: 'none' }, '&:hover': { opacity: 0.8 } }}>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#e8e6df' }}>{e.title}</Typography>
              <Typography sx={{ fontSize: 11, color: 'rgba(232,230,223,0.45)' }}>{formatDate(e.event_date)}{e.city ? ` · ${e.city}` : ''}</Typography>
            </Box>
            {Number(e.is_joined) === 1 && <Typography sx={{ fontSize: 11, color: '#4ade80' }}>✓ Ci sei</Typography>}
          </Box>
        ))
      )}
      <Typography onClick={() => nav('/eventi')} sx={{ fontSize: 12, color: '#d4a550', mt: 1.5, cursor: 'pointer', textAlign: 'right' }}>
        Tutti gli eventi →
      </Typography>
    </Box>
  );
}

export default ProductionHouseView;
